/**
 * Utility functions for delivery date and time calculations
 * Handles timezone-aware date operations for SmartSend delivery scheduling
 *
 * IMPORTANT: All timestamp calculations must use moment-timezone to properly
 * handle timezone conversions. Using JavaScript Date constructor without
 * timezone handling will parse dates in the browser's local timezone,
 * causing incorrect timestamps when the recipient is in a different timezone.
 */

import moment from 'moment-timezone';
import { isDevelopment } from '../env/config';

/**
 * Get today's date at midnight in the specified timezone
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {Date} Date object representing today at 00:00 in the timezone
 */
export const getTodayInTimezone = (timezone) => {
  const now = new Date();

  // Get the date string in the target timezone
  const dateStr = now.toLocaleDateString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // Parse and return as Date object
  const [month, day, year] = dateStr.split('/');
  return new Date(year, month - 1, day);
};

/**
 * Add days to a date
 * @param {Date} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Format date for HTML date input (YYYY-MM-DD)
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display (e.g., "October 20th 2025")
 * @param {Date} date - Date to format
 * @param {string} timezone - IANA timezone
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date, timezone) => {
  return date.toLocaleDateString('en-US', {
    timeZone: timezone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format date for input display (MM/DD/YYYY)
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export const formatDateForInputDisplay = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Get current time + 2 hours in timezone as timestamp
 * @param {string} timezone - IANA timezone
 * @returns {number} Unix timestamp (milliseconds)
 */
export const getCurrentTimePlusTwoHours = (timezone) => {
  const now = new Date();
  const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  return twoHoursLater.getTime();
};

/**
 * Get timestamp for a date at midnight in timezone
 *
 * Uses moment-timezone to correctly parse the date in the recipient's timezone.
 * This matches the original SmartSend implementation pattern:
 *   moment.tz(`${date} 12AM`, timeFormat, timezone).toDate()
 *
 * @param {Date} date - The date
 * @param {string} timezone - IANA timezone (e.g., "America/Los_Angeles")
 * @returns {number} Unix timestamp (milliseconds)
 */
export const getDateTimestamp = (date, timezone) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // moment uses 1-indexed months in string format
  const day = date.getDate();

  // Create date string in M/D/YYYY format (matches original SmartSend pattern)
  const dateStr = `${month}/${day}/${year}`;

  // Parse in the recipient's timezone using moment-timezone
  // This ensures "12/7" in Las Vegas stays as "12/7 00:00 America/Los_Angeles"
  // rather than being interpreted in the browser's local timezone
  const midnightInTimezone = moment.tz(`${dateStr} 12AM`, 'M/D/YYYY hhA', timezone);

  return midnightInTimezone.valueOf();
};

/**
 * Get timestamp for a specific time slot in timezone
 *
 * Uses moment-timezone to correctly calculate the time slot start
 * in the recipient's timezone. This matches the original SmartSend pattern:
 *   moment.tz(`${date} ${hours}`, timeFormat, timezone).toDate()
 *
 * @param {Date} date - The date
 * @param {string} timeSlot - Time slot ('10am-2pm' or '2pm-8pm')
 * @param {string} timezone - IANA timezone (e.g., "America/Los_Angeles")
 * @returns {number} Unix timestamp (milliseconds) - uses start of time slot
 */
export const getTimeSlotTimestamp = (date, timeSlot, timezone) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // moment uses 1-indexed months in string format
  const day = date.getDate();

  // Map time slots to hour values (matches original SmartSend mapping)
  let hour = 10; // Default to 10am
  if (timeSlot === '2pm-8pm') {
    hour = 14; // 2pm
  }

  // Create date string in M/D/YYYY format
  const dateStr = `${month}/${day}/${year}`;

  // Parse in the recipient's timezone using moment-timezone
  // This ensures "12/7 10am" in Las Vegas stays as "12/7 10:00 America/Los_Angeles"
  const timeInTimezone = moment.tz(`${dateStr} ${hour}`, 'M/D/YYYY H', timezone);

  return timeInTimezone.valueOf();
};

/**
 * Check if two dates are the same day
 * @param {Date} date1
 * @param {Date} date2
 * @returns {boolean}
 */
export const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Parse HTML date input value (YYYY-MM-DD) to Date object
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object
 */
export const parseDateInput = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Calculate earliest and latest delivery dates based on offset configuration
 *
 * This follows the same pattern as the original SmartSend project:
 * - Backend calculates offsets from SKU dates (if applicable) and returns them
 * - Frontend calculates dates using: today + offset and today + latestDeliveryOffset
 *
 * All calculations are timezone-aware using the recipient's timezone.
 *
 * @param {number} offset - Minimum days offset from today (already accounts for SKU earliestDeliveryDate if applicable)
 * @param {number} latestDeliveryOffset - Maximum days offset from today (already accounts for SKU latestDeliveryDate if applicable)
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {Object} { earliestDate, latestDate, earliestDateStr, latestDateStr }
 */
export const calculateDateRange = (offset, latestDeliveryOffset, timezone) => {
  // Get today's date in the recipient's timezone
  const today = getTodayInTimezone(timezone);

  // Validate and default offsets to safe values
  const safeOffset = typeof offset === 'number' && !isNaN(offset) && offset >= 0 ? offset : 0;
  const safeLatestOffset = typeof latestDeliveryOffset === 'number' && !isNaN(latestDeliveryOffset) && latestDeliveryOffset >= 0
    ? latestDeliveryOffset
    : 7; // Default to 7 days if invalid

  // Calculate dates: today + offset (same pattern as original SmartSend)
  const earliestDate = addDays(today, safeOffset);
  const latestDate = addDays(today, safeLatestOffset);

  // Ensure earliest date is not after latest date
  // This can happen if offset > latestDeliveryOffset (backend should prevent this, but we handle it gracefully)
  if (earliestDate > latestDate) {
    if (isDevelopment()) {
      console.warn('Invalid date range: offset exceeds latestDeliveryOffset', {
        offset: safeOffset,
        latestDeliveryOffset: safeLatestOffset,
        earliestDate,
        latestDate,
      });
    }
    // Use today as earliest, and latest as calculated (or today if that's also invalid)
    const finalEarliest = today;
    const finalLatest = latestDate >= today ? latestDate : today;

    return {
      earliestDate: finalEarliest,
      latestDate: finalLatest,
      earliestDateStr: formatDateForInput(finalEarliest),
      latestDateStr: formatDateForInput(finalLatest),
    };
  }

  // Ensure dates are not in the past (defensive check)
  const finalEarliest = earliestDate >= today ? earliestDate : today;
  const finalLatest = latestDate >= today ? latestDate : today;

  // Final validation: ensure earliest <= latest
  if (finalEarliest > finalLatest) {
    // Last resort: use today as both bounds
    if (isDevelopment()) {
      console.warn('Date range validation failed, using today as both bounds', {
        finalEarliest,
        finalLatest,
        today,
      });
    }
    return {
      earliestDate: today,
      latestDate: today,
      earliestDateStr: formatDateForInput(today),
      latestDateStr: formatDateForInput(today),
    };
  }

  return {
    earliestDate: finalEarliest,
    latestDate: finalLatest,
    earliestDateStr: formatDateForInput(finalEarliest),
    latestDateStr: formatDateForInput(finalLatest),
  };
};
